/**
 * Service para Eventos de Formulários
 * 
 * Centraliza a lógica de buscar e executar formulários baseados em eventos.
 * 
 * Uso:
 *   import { buscarFormularioEvento, EVENTOS } from '../services/eventosForm';
 *   
 *   const formulario = await buscarFormularioEvento(EVENTOS.ON_APPROVE_LAUDO, programaId);
 *   if (formulario) {
 *       // Mostrar modal com formulário
 *   }
 */

import api from './api';

/**
 * Mapeamento de eventos disponíveis no sistema
 * Adicione novos eventos aqui conforme necessário
 */
export const EVENTOS = {
    ON_APPROVE_LAUDO: 'onApproveLaudo',
    // Eventos futuros:
    // ON_CONFIRM_SERVICE: 'onConfirmService',
    // ON_START_ATENDIMENTO: 'onStartAtendimento',
    // ON_FINISH_ATENDIMENTO: 'onFinishAtendimento',
    // ON_CREATE_RELATO: 'onCreateRelato',
};

/**
 * Lista de eventos para exibição em dropdowns/selects
 */
export const EVENTOS_LIST = [
    { value: EVENTOS.ON_APPROVE_LAUDO, label: 'Aprovação de Laudo' },
    // { value: EVENTOS.ON_CONFIRM_SERVICE, label: 'Confirmar Realização de Serviço' },
];

/**
 * Carrega a lista de tipos de eventos disponíveis do backend
 * @returns {Promise<Array>} Lista de eventos [{value, label}, ...]
 */
export const carregarTiposEventos = async () => {
    try {
        const response = await api.get('/formularios-eventos/tipos/');
        return response.data;
    } catch (error) {
        console.error('[eventosForm] Erro ao carregar tipos de eventos, usando fallback:', error);
        return EVENTOS_LIST; // Fallback para lista estática
    }
};

/**
 * Busca o formulário configurado para um evento e programa específico
 * 
 * @param {string} evento - Slug do evento (use EVENTOS.XXX)
 * @param {number|null} programaId - ID do programa (opcional)
 * @returns {Promise<object|null>} Formulário ou null se não existir
 */
export const buscarFormularioEvento = async (evento, programaId = null) => {
    try {
        let url = `/formularios-eventos/?evento=${evento}`;
        if (programaId) {
            url += `&programa_id=${programaId}`;
        }

        const response = await api.get(url);
        
        // Se retornar lista vazia, não tem formulário
        if (response.data && response.data.results && response.data.results.length === 0) {
            return null;
        }
        
        // Pega o primeiro formulário da lista (mais específico - com programa_id)
        const formulario = response.data?.results?.[0] || response.data;
        
        if (!formulario) {
            return null;
        }

        // Verificar se está ativo
        if (formulario.ativo === false) {
            return null;
        }

        return formulario;
    } catch (error) {
        // 404 = não encontrado, tratamos como "sem formulário"
        if (error.response?.status === 404 || error.response?.status === 204) {
            return null;
        }
        
        console.error('[eventosForm] Erro ao buscar formulário:', error);
        throw error;
    }
};

/**
 * Verifica se existe um formulário para o evento/programa
 * 
 * @param {string} evento - Slug do evento
 * @param {number|null} programaId - ID do programa
 * @returns {Promise<boolean>}
 */
export const existeFormularioEvento = async (evento, programaId = null) => {
    const formulario = await buscarFormularioEvento(evento, programaId);
    return formulario !== null;
};

/**
 * Retorna o label de um evento pelo seu slug
 * 
 * @param {string} eventoSlug 
 * @returns {string}
 */
export const getEventoLabel = (eventoSlug) => {
    const evento = EVENTOS_LIST.find(e => e.value === eventoSlug);
    return evento ? evento.label : eventoSlug;
};

export default {
    EVENTOS,
    EVENTOS_LIST,
    buscarFormularioEvento,
    existeFormularioEvento,
    getEventoLabel,
    carregarTiposEventos,
};
